import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiGrid, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { Play, Loader2, Radio } from "lucide-react";

export function Scenarios() {
    const [inp, setInp] = useState({ cu_price_usd_lb: 4.2, co_price_usd_lb: 18.0, energy_price_usd_kwh: 0.11, throughput_pct: 100, cu_recovery_pct: 89, co_recovery_pct: 72, shutdown_days: 0 });
    const [result, setResult] = useState(null);
    const [presets, setPresets] = useState([]);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/scenarios/library").then(({ data }) => setPresets(data.scenarios || [])); }, []);
    async function run() {
        setBusy(true);
        try { const { data } = await api.post("/scenarios/simulate", inp); setResult(data); }
        finally { setBusy(false); }
    }
    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);
    function loadPreset(p) { const { id, name, ...vals } = p; setInp(vals); setTimeout(run, 100); }

    const sliders = [
        { key: "cu_price_usd_lb", label: "Copper price (US$/lb)", min: 2.5, max: 6.0, step: 0.05 },
        { key: "co_price_usd_lb", label: "Cobalt price (US$/lb)", min: 8, max: 30, step: 0.5 },
        { key: "energy_price_usd_kwh", label: "Energy price (US$/kWh)", min: 0.06, max: 0.28, step: 0.01 },
        { key: "throughput_pct", label: "Throughput vs plan (%)", min: 70, max: 115, step: 1 },
        { key: "cu_recovery_pct", label: "Cu recovery (%)", min: 82, max: 94, step: 0.2 },
        { key: "co_recovery_pct", label: "Co recovery (%)", min: 60, max: 82, step: 0.5 },
        { key: "shutdown_days", label: "Shutdown days this month", min: 0, max: 30, step: 1 },
    ];

    return (
        <div className="space-y-6">
            <ModuleHeader kicker="Executive Scenario Planning"
                title="What-if models · Copper · Cobalt · Energy · Throughput · Shutdown"
                description="Interactively model financial impact before decisions. All models are advisory — implementation requires CFO and COO approval."
            />
            <div className="flex flex-wrap gap-2">
                {presets.map((p) => (
                    <button key={p.id} onClick={() => loadPreset(p)} className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:border-kcc-copper/50 text-kcc-silver hover:text-white" data-testid={`preset-${p.id}`}>{p.name}</button>
                ))}
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <Panel title="Scenario inputs" accent="cobalt">
                    <div className="space-y-4">
                        {sliders.map((s) => (
                            <div key={s.key} data-testid={`slider-${s.key}`}>
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="kcc-text-silver">{s.label}</span>
                                    <span className="font-mono-kcc text-white">{inp[s.key]}</span>
                                </div>
                                <input type="range" min={s.min} max={s.max} step={s.step} value={inp[s.key]}
                                    onChange={(e) => setInp({ ...inp, [s.key]: parseFloat(e.target.value) })}
                                    className="w-full accent-kcc-copper" />
                            </div>
                        ))}
                        <button onClick={run} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-kcc-copper hover:bg-kcc-copper-light text-white rounded-md px-4 py-2 text-sm mt-2" data-testid="run-scenario">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Simulate
                        </button>
                    </div>
                </Panel>
                <Panel title="Financial impact" accent="copper" className="xl:col-span-2">
                    {!result ? <div className="kcc-text-silver text-sm">Simulate to see results…</div> : (
                        <div className="space-y-4" data-testid="scenario-result">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                    ["Revenue", `$${result.scenario.revenue_musd}M`, result.delta.revenue_musd],
                                    ["EBITDA", `$${result.scenario.ebitda_musd}M`, result.delta.ebitda_musd],
                                    ["OPEX", `$${result.scenario.opex_musd}M`, null],
                                    ["Cash cost Cu", `$${result.scenario.cash_cost_cu_usd_lb}/lb`, null],
                                    ["Cu sold", `${result.scenario.cu_sold_tonnes.toLocaleString()} t`, null],
                                    ["Co sold", `${result.scenario.co_sold_tonnes} t`, null],
                                    ["Energy cost", `$${result.scenario.energy_cost_musd}M`, null],
                                    ["Δ Revenue", `$${result.delta.revenue_musd}M`, null],
                                ].map(([k, v, delta], i) => (
                                    <div key={i} className="kcc-surface-elev p-3">
                                        <div className="kcc-label">{k}</div>
                                        <div className="font-display text-lg text-white mt-1">{v}</div>
                                        {delta !== null && <div className={`text-[11px] mt-0.5 ${delta > 0 ? "text-kcc-green" : delta < 0 ? "text-kcc-red" : "kcc-text-silver"}`}>{delta > 0 ? "+" : ""}{delta}M vs baseline</div>}
                                    </div>
                                ))}
                            </div>
                            <div className="text-[11px] kcc-text-dim border-t border-white/5 pt-3">
                                Baseline (latest month): Revenue ${result.baseline.revenue_musd}M · EBITDA ${result.baseline.ebitda_musd}M · Cu cash cost ${result.baseline.cash_cost_cu}/lb
                            </div>
                            <div className="text-[11px] text-kcc-amber">{result.governance}</div>
                        </div>
                    )}
                </Panel>
            </div>
        </div>
    );
}

export function IntegrationHub() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/integrations/health").then(({ data }) => setData(data)); }, []);
    return (
        <div className="space-y-6">
            <ModuleHeader kicker="Industrial Integration Hub"
                title="One Hub · SCADA · PI · SAP · LIMS · Maximo · GIS · Fleet · IoT · Identity"
                description="Central connector platform for every enterprise system — health, throughput, latency, error rates and last message. Fictional demo data is replaced automatically when a live connector is enabled."
            />
            <KpiGrid kpis={data?.kpis || []} accent="cobalt" cols={6} />
            <Panel title="Enterprise Integrations · Live Status" accent="cobalt">
                <DataTable
                    columns={[
                        { key: "tag", label: "Tag" },
                        { key: "name", label: "Connector" },
                        { key: "product", label: "Product" },
                        { key: "type", label: "Type" },
                        { key: "protocol", label: "Protocol" },
                        { key: "environment", label: "Env" },
                        { key: "messages_24h", label: "Msgs / 24h", render: (r) => r.messages_24h.toLocaleString() },
                        { key: "avg_latency_ms", label: "Latency (ms)" },
                        { key: "error_rate_pct", label: "Errors %" },
                        { key: "last_message", label: "Last msg", render: (r) => r.last_message.slice(11, 19) + "Z" },
                        { key: "status", label: "Health", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                        { key: "owner", label: "Owner" },
                    ]}
                    rows={data?.integrations || []}
                    testid="integrations-table"
                />
            </Panel>
            <div className="kcc-panel p-4 flex items-start gap-3">
                <Radio className="w-5 h-5 text-kcc-cobalt-light mt-0.5" />
                <div className="text-xs kcc-text-silver">
                    Every integration is authenticated, rate-limited, schema-validated, retried and dead-lettered by the Integration Hub. All events are audit-logged and available for AI grounding across every KCC360 workspace.
                </div>
            </div>
        </div>
    );
}
